import { FC, useEffect, useState } from 'react';

import { useCurrentUser } from '@queries/UserQueries';

import { useUpdateProfileMutation } from '@mutations/UserMutations';

import { useAnalytics } from '@hooks/useAnalytics';

import Button from '@components/buttons/Button';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

interface UserOnboardingGatePopupContentProps {
  onClose: () => void;
}

const UserOnboardingGatePopupContent: FC<UserOnboardingGatePopupContentProps> = ({ onClose }) => {
  const { track } = useAnalytics();

  const [userProperties, setUpdatedUserProperties] = useState({
    firstName: '',
    lastName: '',
  });
  const { isSubmitting, updateProfileAsync } = useUpdateProfileMutation();

  const handleContinue = async () => {
    track('Light User Onboarding', {
      firstName: userProperties.firstName,
      lastName: userProperties.lastName,
    });

    if (userProperties.firstName && userProperties.lastName) {
      await updateProfileAsync(userProperties);
      onClose();
    }
  };

  return (
    <>
      <TitleToolbar text={`Finish Account Setup`} size="md" />
      <p className="mt-0">
        Your profile is almost ready! Please fill in your first and last name so that your
        connections can know who you are.
      </p>
      <p>This can be your stage name or what your friends call you.</p>

      <div className="d-grid gap-2">
        <div>
          <FormLabel text="First Name *" />
          <Input
            name="firstName"
            onChange={(e) =>
              setUpdatedUserProperties((prevValue) => ({
                ...prevValue,
                firstName: e.target.value,
              }))
            }
          />
        </div>
        <div>
          <FormLabel text="Last Name *" />
          <Input
            type="text"
            name="lastName"
            onChange={(e) =>
              setUpdatedUserProperties((prevValue) => ({
                ...prevValue,
                lastName: e.target.value,
              }))
            }
          />
        </div>
        <div>
          <Button
            roundness="rounded"
            onClick={handleContinue}
            disabled={!userProperties.firstName || !userProperties.lastName || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Finish Setup'}
          </Button>
        </div>
      </div>
    </>
  );
};

export const UserOnboardingGatePopup = () => {
  const { currentUser } = useCurrentUser();
  const [isVisible, setIsVisible] = useState(false);

  const handleClose = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    if (currentUser?._id && !currentUser?.privateDisplayName) {
      setIsVisible(true);
    }
  }, [currentUser]);

  return (
    <Popup isVisible={isVisible} onClose={handleClose}>
      <Popup.Content>
        {isVisible && <UserOnboardingGatePopupContent onClose={handleClose} />}
      </Popup.Content>
    </Popup>
  );
};
